import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import './TagTester.css';

interface ScanListItem {
  id: string;
  status: string;
  mode: string;
  riskLevel: string;
  riskScore: number;
  createdAt: string;
  artifact: {
    sourceType: string;
    vendorGuess?: string;
  };
}

export function TagTesterHistory(): JSX.Element {
  const navigate = useNavigate();
  const [scans, setScans] = useState<ScanListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchScans = async () => {
      try {
        const response = await fetch(apiUrl('/tag-scans'));
        if (!response.ok) throw new Error('Failed to fetch scans');

        const data = await response.json();
        setScans(data.data?.scans || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchScans();

    const interval = setInterval(fetchScans, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRowClick = (scanId: string) => {
    navigate(`/tag-tester/${scanId}`);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="tag-tester-history">
      <div className="history-header">
        <h1>Scan History</h1>
        <button
          className="new-scan-button"
          onClick={() => navigate('/tag-tester')}
        >
          + New Scan
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading scans...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
        </div>
      ) : scans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h2>No scans yet</h2>
          <p>Create your first tag scan to get started</p>
          <button
            className="primary-button"
            onClick={() => navigate('/tag-tester')}
          >
            Start Scanning
          </button>
        </div>
      ) : (
        <div className="history-table-wrapper">
          <table className="history-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Risk Level</th>
                <th>Score</th>
                <th>Vendor</th>
                <th>Mode</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan) => (
                <tr
                  key={scan.id}
                  className="history-row clickable"
                  onClick={() => handleRowClick(scan.id)}
                >
                  <td className="label-cell">{scan.id.slice(0, 8)}...</td>
                  <td>
                    <span className="type-badge">{scan.artifact?.sourceType || '—'}</span>
                  </td>
                  <td>
                    {scan.riskLevel ? (
                      <span className={`risk-badge risk-${scan.riskLevel}`}>
                        {scan.riskLevel.charAt(0).toUpperCase() + scan.riskLevel.slice(1)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="score-cell">{scan.riskScore ?? '—'}</td>
                  <td>{scan.artifact?.vendorGuess || '—'}</td>
                  <td>{scan.mode}</td>
                  <td>
                    <span className={`status-badge status-${scan.status}`}>
                      {scan.status.charAt(0).toUpperCase() + scan.status.slice(1)}
                    </span>
                  </td>
                  <td className="date-cell">{formatDate(scan.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
